import React, { useEffect } from "react";
import { useOrganisation } from "@api/hooks";

/**
 * Escape hook.
 *
 * https://stackoverflow.com/a/59963095
 * https://creativecommons.org/licenses/by-sa/4.0/
 */
export const useEscape = (onEscape: () => void) => {
	useEffect(() => {
		const handleEsc = (event: any) => {
			if (event.keyCode === 27) onEscape();
		};
		window.addEventListener("keydown", handleEsc);

		return () => {
			window.removeEventListener("keydown", handleEsc);
		};
	}, [onEscape]);
};

/**
 * https://www.30secondsofcode.org/react/s/use-click-outside
 * @param ref
 * @param callback
 */
export const useClickOutside = (ref: React.RefObject<any>, callback: () => any) => {
	const handleClick = (e: any) => {
		if (ref.current && !ref.current.contains(e.target)) {
			callback();
		}
	};
	React.useEffect(() => {
		document.addEventListener("click", handleClick);
		return () => {
			document.removeEventListener("click", handleClick);
		};
	});
};

type CompetenceNameByLang = {
	sv?: string;
	key: string;
};

type CompetenceWithName = {
	branch: string;
	branchName?: CompetenceNameByLang;
	service: string;
	serviceName?: CompetenceNameByLang;
	skills?: string[];
	skillsNames?: CompetenceNameByLang[];
	supplement?: number;
};

/**
 * Appends translated names to the competence.
 * @param competence
 * @returns
 */
export const useTranslatedCompetence = (competence: any) => {
	const { organisation } = useOrganisation();

	return competence?.map((competence: any) => {
		let out: CompetenceWithName = {
			...competence,
		};

		const branch = organisation?.competences.find((c) => c.key === competence.branch);

		if (!branch) {
			return out;
		}

		out.branchName = { ...branch.name, key: competence.branch };

		const service = branch.items?.find((s) => s.key === competence.service);

		if (service) {
			out.serviceName = { ...service.name, key: competence.service };

			if (competence.skills && service.items) {
				out.skillsNames = competence.skills.map((skill: string) => {
					const s = service.items?.find((s) => s.key === skill);
					return s?.name ? { ...s.name, key: skill } : { sv: skill, key: skill };
				});
			}
		}

		return out;
	});
};
